import { useEffect, useState } from "react";
import { api, brl, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { TrendingUp, Loader2 } from "lucide-react";
import { PageHead, Stat, Empty, RangePicker, btnGhost, monthStartIso, todayIso, qtyFmt } from "@/components/erp/ErpUi";
import { CATEGORY_LABEL } from "@/pages/OwnerFinance";

function RankTable({ title, rows, testid, showQty = true }) {
  return (
    <section data-testid={testid} className="rounded-2xl border border-zinc-800 overflow-x-auto">
      <div className="px-3 py-2 bg-zinc-900 text-xs font-mono-caps uppercase text-zinc-400">{title}</div>
      {rows.length === 0 ? <p className="p-4 text-sm text-zinc-500">Sem dados no período.</p> : (
        <table className="w-full text-sm min-w-[600px]">
          <thead><tr className="text-[11px] font-mono-caps text-zinc-500 uppercase">
            <th className="text-left px-3 py-2">Item</th>{showQty && <th className="text-right px-3 py-2">Qtd</th>}
            <th className="text-right px-3 py-2">Receita</th><th className="text-right px-3 py-2">Custo</th><th className="text-right px-3 py-2">Lucro</th><th className="text-right px-3 py-2">Margem</th><th className="text-right px-3 py-2">Markup</th>
          </tr></thead>
          <tbody>{rows.map((r, i) => (
            <tr key={r.product_id || r.description || i} className="border-t border-zinc-800">
              <td className="px-3 py-2 text-zinc-100">{r.description || "—"}</td>{showQty && <td className="px-3 py-2 text-right text-zinc-300">{qtyFmt(r.qty)}</td>}
              <td className="px-3 py-2 text-right text-zinc-100">{brl(r.revenue)}</td><td className="px-3 py-2 text-right text-zinc-400">{brl(r.cost)}</td>
              <td className={`px-3 py-2 text-right font-bold ${r.profit < 0 ? "text-red-300" : "text-green-neon"}`}>{brl(r.profit)}</td>
              <td className="px-3 py-2 text-right text-zinc-300">{r.margin.toFixed(1)}%</td>
              <td className="px-3 py-2 text-right text-amber-brand">{r.markup == null ? "—" : `${r.markup.toFixed(1)}%`}</td>
            </tr>))}</tbody>
        </table>
      )}
    </section>
  );
}

export default function OwnerProfit() {
  const { user } = useAuth();
  const multi = (user?.shops || []).length > 1;
  const [range, setRange] = useState([monthStartIso(), todayIso()]);
  const [scope, setScope] = useState(user?.shop_id === "all" ? "all" : "current");
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get("/erp/profitability", { params: { from: range[0], to: range[1], scope } }).then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e)));
  }, [range, scope]);
  const t = data?.totals;
  return (
    <div className="space-y-5 animate-fade-up">
      <PageHead icon={TrendingUp} kicker="Rentabilidade" title="Lucro do período">
        <RangePicker from={range[0]} to={range[1]} onChange={setRange} testid="profit-range" />
        {multi && ["current", "all"].map((s) => (
          <Button key={s} data-testid={`profit-scope-${s}`} variant="outline" onClick={() => setScope(s)} className={scope === s ? "bg-amber-brand text-black font-bold hover:bg-amber-brandDark" : btnGhost}>{s === "all" ? "Todas" : "Esta unidade"}</Button>
        ))}
      </PageHead>
      {!data ? <Loader2 className="h-6 w-6 animate-spin text-green-brand mx-auto" /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
            <Stat testid="profit-revenue" label="Faturamento" value={brl(t.revenue)} />
            <Stat testid="profit-parts" label="Custo das peças" value={brl(t.parts_cost)} tone="red" />
            <Stat testid="profit-third-party" label="Terceirizados" value={brl(t.third_party)} tone="red" />
            <Stat testid="profit-commissions" label="Comissões das OS" value={brl(t.commissions)} tone="red" />
            <Stat testid="profit-expenses" label="Despesas" value={brl(t.expenses)} tone="red" />
            <Stat testid="profit-total" label={`Lucro · ${t.margin.toFixed(1)}%`} value={brl(t.profit)} tone={t.profit < 0 ? "red" : "green"} />
          </div>
          <p className="text-[11px] text-zinc-500">Lucro = faturamento − custo das peças vendidas − custo dos terceirizados − comissões das OS − despesas lançadas no financeiro (exceto compras de peças e terceirizados, que já entram pelo custo). Não lance a comissão da OS de novo em contas a pagar.</p>
          {data.by_shop.length > 1 && (
            <section data-testid="profit-by-shop" className="rounded-2xl border border-zinc-800 overflow-x-auto">
              <table className="w-full text-sm min-w-[560px]">
                <thead><tr className="bg-zinc-900 text-[11px] font-mono-caps text-zinc-500 uppercase"><th className="text-left px-3 py-2">Unidade</th><th className="text-right px-3 py-2">Faturamento</th><th className="text-right px-3 py-2">Peças</th><th className="text-right px-3 py-2">Comissões</th><th className="text-right px-3 py-2">Despesas</th><th className="text-right px-3 py-2">Lucro</th></tr></thead>
                <tbody>{data.by_shop.map((s) => (
                  <tr key={s.shop_id} className="border-t border-zinc-800"><td className="px-3 py-2 text-zinc-100">{s.shop_name}</td><td className="px-3 py-2 text-right">{brl(s.revenue)}</td><td className="px-3 py-2 text-right text-zinc-400">{brl(s.parts_cost)}</td><td className="px-3 py-2 text-right text-zinc-400">{brl(s.commissions)}</td><td className="px-3 py-2 text-right text-zinc-400">{brl(s.expenses)}</td><td className={`px-3 py-2 text-right font-bold ${s.profit < 0 ? "text-red-300" : "text-green-neon"}`}>{brl(s.profit)}</td></tr>
                ))}</tbody>
              </table>
            </section>
          )}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <RankTable testid="profit-top-sold" title="Peças mais vendidas" rows={data.top_sold} />
            <RankTable testid="profit-top-profit" title="Peças mais lucrativas" rows={data.top_profit} />
          </div>
          <RankTable testid="profit-services" title="Serviços (mão de obra)" rows={data.services} />
          {data.expenses_by_category.length > 0 ? (
            <section data-testid="profit-expenses-cat" className="rounded-2xl border border-zinc-800 p-3">
              <div className="text-xs font-mono-caps uppercase text-zinc-400 mb-2">Despesas por categoria</div>
              <ul className="space-y-1 text-sm">{data.expenses_by_category.map((e) => <li key={e.category} className="flex justify-between"><span className="text-zinc-300">{CATEGORY_LABEL[e.category] || e.category}</span><span className="text-red-300">{brl(e.amount)}</span></li>)}</ul>
            </section>
          ) : <Empty>Nenhuma despesa lançada no período.</Empty>}
        </>
      )}
    </div>
  );
}
